import { useEffect, useState } from "react";
import { Section, Field } from "@/components/Shell";
import { LogoFrame, MagnaMentesMark } from "@/components/Logos";
import { api, formatApiError } from "@/lib/api";
import { useBranding } from "@/context/BrandingContext";
import { toast } from "sonner";
import { Loader2, Save, Upload, Trash2 } from "lucide-react";

const MAX_LOGO_BYTES = 300 * 1024; // 300 KB

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ""));
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function AdminBrandingPage() {
  const branding = useBranding();
  const [displayName, setDisplayName] = useState("");
  const [tagline, setTagline] = useState("");
  const [logoDataUrl, setLogoDataUrl] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (branding.loaded) {
      setDisplayName(branding.display_name || "");
      setTagline(branding.tagline || "");
      setLogoDataUrl(branding.logo_data_url || null);
    }
  }, [branding.loaded, branding.display_name, branding.tagline, branding.logo_data_url]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_LOGO_BYTES) {
      toast.error("Logo must be under 300KB");
      return;
    }
    const url = await fileToDataUrl(file);
    setLogoDataUrl(url);
  };

  const clearLogo = () => setLogoDataUrl("");

  const save = async () => {
    if (!displayName.trim()) return toast.error("Display name is required");
    setSaving(true);
    try {
      await api.put("/admin/branding", {
        display_name: displayName.trim(),
        tagline: tagline.trim() || null,
        logo_data_url: logoDataUrl === "" ? null : logoDataUrl,
      });
      toast.success("Branding updated");
      branding.refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const preview = logoDataUrl;

  return (
    <Section
      kicker="Brand identity"
      title="Magna Mentes branding"
    >
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="brut-card p-5">
          <div className="font-mono text-[11px] uppercase tracking-[0.22em] mb-4">
            Edit branding
          </div>
          <div className="space-y-4">
            <Field label="Display name" required>
              <input
                className="input-brut"
                data-testid="branding-display-name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Magna Mentes"
              />
            </Field>
            <Field label="Tagline" hint="Appears under the logo in the header">
              <input
                className="input-brut"
                data-testid="branding-tagline"
                value={tagline}
                onChange={(e) => setTagline(e.target.value)}
                placeholder="Field Recruitment OS"
              />
            </Field>
            <Field label="Logo" hint="PNG / JPG / SVG · max 300KB · square works best">
              <div className="flex flex-wrap items-center gap-3">
                <label className="btn-brut cursor-pointer">
                  <Upload size={14} /> Upload logo
                  <input
                    type="file"
                    accept="image/*,.svg"
                    className="hidden"
                    onChange={onFile}
                    data-testid="branding-logo-input"
                  />
                </label>
                {preview && (
                  <button
                    type="button"
                    className="btn-brut btn-ghost text-xs"
                    onClick={clearLogo}
                    data-testid="branding-clear-logo"
                  >
                    <Trash2 size={12} /> Remove
                  </button>
                )}
              </div>
            </Field>
          </div>
          <div className="mt-6">
            <button
              className="btn-brut btn-flame"
              onClick={save}
              disabled={saving}
              data-testid="branding-save"
            >
              {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
              Save branding
            </button>
          </div>
        </div>

        <div className="brut-card p-5">
          <div className="font-mono text-[11px] uppercase tracking-[0.22em] mb-4">
            Live preview
          </div>
          <div className="border-2 border-black bg-[rgb(var(--paper))] p-4 flex items-center gap-3 mb-4">
            <LogoFrame
              src={preview || null}
              alt="Preview"
              size={44}
              fallback={<MagnaMentesMark size={44} />}
            />
            <div className="leading-tight">
              <div className="font-display text-xl uppercase">{displayName || "Magna Mentes"}</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-black/60">
                {tagline || "Field Recruitment OS"}
              </div>
            </div>
          </div>
          <div className="text-xs text-black/70 font-mono">
            This is exactly how the header will render across the site.
          </div>
        </div>
      </div>
    </Section>
  );
}
